<script lang="ts">
    import { getContext, onDestroy } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import type { Readable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'

    import { WDATA } from 'hydria/context'
    import { gettext as _, ngettext, resolve } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, groups as userGroups } from 'hydria/services/session'
    import { sanchor, sgoto, spath } from 'hydria/svelte_helpers'

    import { statusText } from 'admin/routes/Ecom/Order/order'

    import type { User, UserHandler } from './user'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import StatusBlockNotifications from './StatusBlockNotifications.svelte'

    const userh = getContext('User') as UserHandler

    const status = sderived(userh.status, ($status) => $status)
    const loading = sderived(userh.loading, ($loading) => $loading)

    const user: Readable<User> = sderived(userh.userWritable, ($v) => $v)

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    let lockWorking = $state(false)

    async function unlockUser() {
        lockWorking = true

        try {
            await simpleFetch('DELETE', `/json/wdom-user/1/${$user.id}/password-lock`)
            userh.raw.item.locked = false
            userh.loadUserResponse()

            notify({
                type: 'success',
                title: 'User unlock',
                text: 'Le compte de l’utilisateur est débarré. Un email approprié a été envoyé à l’utilisateur.',
            })
        } catch (e) {
            console.error('Failed to unlock acount:', e)
            notify({
                type: 'danger',
                title: 'User unlock',
                text: 'Le compte l’a pas pu être débarré suite à une erreur.',
            })
        }

        lockWorking = false
    }

    let emailPwStatus: null | 'working' | 'confirm' | 'sent' = $state(null)

    $effect(() => {
        if (emailPwStatus === 'sent') {
            setTimeout(() => (emailPwStatus = null), 3000)
        }
    })

    async function sendPasswordByEmail() {
        emailPwStatus = 'working'

        try {
            await simpleFetch('PUT', `/json/wdom-user/1/${$user.id}/send-password-email`)
            emailPwStatus = 'sent'
        } catch (e) {
            console.error('Failed to send email:', e)
            notify({
                type: 'danger',
                title: 'Auto-signin',
                text: 'Un courriel n’a pas pu être envoyé.',
            })
            emailPwStatus = null
        }
    }

    let signinToken: null | 'working' | string = $state(null)
    // Note: un token reste valide pour 30 secondes
    let signinTokenTimer: ReturnType<typeof setTimeout> | undefined = undefined

    async function generateSigninToken() {
        signinToken = 'working'

        try {
            const r = await simpleFetch('POST', `/json/wdom-user/1/${$user.id}/auto-signin-token`)
            signinToken = r.data.token
            setTokenTimer()
        } catch (e: any) {
            console.error('Failed to generate auto-signin token:', e)
            notify({
                type: 'danger',
                title: 'Auto-signin',
                text: 'Un jeton de connexion n’a pas pu être généré.',
            })
        }
    }

    function clearTokenTimer() {
        if (signinTokenTimer) {
            clearTimeout(signinTokenTimer)
            signinTokenTimer = undefined
        }
    }
    function setTokenTimer() {
        clearTokenTimer()
        if (signinToken != null && signinToken !== 'working') {
            signinTokenTimer = setTimeout(() => {
                signinTokenTimer = undefined
                if (signinToken != null && signinToken !== 'working') signinToken = null
            }, 25000)
        }
    }
    function clearSigninToken() {
        clearTokenTimer()
        signinToken = null
    }

    let activeWorking = $state(false)
    async function setUserActive(nv: boolean) {
        activeWorking = true

        try {
            const r = await simpleFetch(nv ? 'PUT' : 'DELETE', `/json/wdom-user/1/${$user.id}/active`)
            userh.raw.item.active = r.data.active
            userh.loadUserResponse()

            notify({
                type: 'success',
                title: 'User actif',
                text: 'L’utilisateur est maintenant ' + (nv ? 'actif' : 'non-actif') + '.',
            })
        } catch (e) {
            console.error('Failed to set active state:', e)
            notify({
                type: 'danger',
                title: 'User actif',
                text: 'L’état « actif » n’a pas pu être changé',
            })
        }

        activeWorking = false
    }

    let ecomStats: { [key: string]: number } = $state({})
    let ecomTotal: number = $state(0)
    let ecomStatsWorking = $state(true)
    async function loadEcomStats(userId: string) {
        ecomStatsWorking = true

        try {
            const r = await simpleFetch('GET', `/json/ecom/1/user-stats/${userId}`)
            ecomStats = r.data.status_counts
            ecomTotal = Object.values(ecomStats).reduce((ps, n) => ps + n, 0)
        } catch (e) {
            console.error('Failed so retrieve ecom stats:', e)
            ecomStats = {}
            ecomTotal = 0
        }

        ecomStatsWorking = false
    }

    let loadedUserId: string | undefined
    unsubs.push(
        user.subscribe(($user) => {
            if ($user && $user.id && loadedUserId != $user.id) {
                loadedUserId = $user.id
                loadEcomStats($user.id)
            }
        }),
    )

    let deletingUser: undefined | 'confirm' | 'working' = $state()
    let deleteReason: string = $state('')
    let cleanDeleteReason: string = $derived(deleteReason.trim())

    async function deleteUser() {
        deletingUser = 'working'

        try {
            await simpleFetch('DELETE', `/json/wdom-user/1/${$user.id}`, { reason: cleanDeleteReason })
            notify({
                type: 'success',
                title: 'Suppression d’un utilisateur',
                text: 'L’utilisateur a été supprimé.',
            })
            sgoto('/wdom-users')
        } catch (e) {
            console.error('Error deleting user:', e)
            notify({
                type: 'danger',
                title: 'Suppression d’un utilisateur',
                text: 'L’utilisateur n’a pas pu être supprimé.',
            })

            deletingUser = undefined
        }
    }

    let creatingOrder = $state(false)

    async function createOrder() {
        creatingOrder = true

        try {
            const r = await simpleFetch('POST', '/json/ecom/1/order', { user_id: $user.id })
            sgoto(`/ecom/order/${r.data.order.id}`)
        } catch (e) {
            console.error('Failed to create order:', e)
            notify({
                type: 'danger',
                title: 'Commande',
                text: 'Une nouvelle commande n’a pas pu être créée.',
            })
        }

        creatingOrder = false
    }

    unsubs.push(
        user.subscribe(($user) => {
            if (!$user) return
        }),
    )
</script>

<div class="card">
    <div class="card-title text-primary">{$_('User status')}</div>
    {#if $status == 'loading' || $loading}
        <div class="card-body">
            <LoadingPattern pattern={[20, 30, 30, 30, 30, '-', 20, 40, 10, 30, 30, '-', 25, 25, '-', 50, 'x', 'x', 'x', 'x', 'x', 20, 50, '-', 25]} />
        </div>
    {:else}
        <div class="card-body">
            <dl>
                <dt>{$_('Password')}</dt>
                <dd>
                    <ul class="list-unstyled">
                        <li>
                            {#if $user.has_password}
                                <span class="text-success"><Icon inline icon="fa:check-square-o" /> {$_('The user has a password')}</span>
                            {:else}
                                <span class="text-danger"><Icon inline icon="fa:square-o" /> {$_('The user does not have a password')}</span>
                            {/if}
                        </li>
                        <li class:text-danger={$user.locked}>
                            {#if lockWorking}
                                <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                            {:else}
                                <Icon icon={$user.locked ? 'fa:lock' : 'fa:unlock-alt'} />
                            {/if}
                            {#if $user.locked}
                                {$_('The user’s account is locked, and cannot sign in.')}
                                <button class="btn btn-link btn-a" type="button" onclick={() => unlockUser()} disabled={lockWorking}>
                                    {#if lockWorking}
                                        {$_('Unlocking the user’s account…')}
                                    {:else}
                                        {$_('Unlock the user’s account')}
                                    {/if}
                                </button>
                            {:else}
                                {$_('The user’s account is unlocked, and can sign in.')}
                            {/if}
                        </li>

                        <li style="display: flex; gap: 2em; align-items: center;">
                            <div style="flex: 1 0 0;">
                                {#if emailPwStatus == 'working'}
                                    <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                {:else}
                                    <Icon class="text-primary" inline icon="fa:envelope" />
                                {/if}
                                <button class="btn btn-link btn-a" type="button" disabled={emailPwStatus != null} onclick={() => (emailPwStatus = 'confirm')}>{$_('Send password reset email')}</button>
                            </div>
                            <div style="flex: 0 0 auto;">
                                {#if emailPwStatus == 'confirm'}
                                    <div class="btn-group btn-group-sm">
                                        <button type="button" class="btn btn-outline-secondary" onclick={() => (emailPwStatus = null)}>{$_('Cancel')}</button>
                                        <button type="button" class="btn btn-primary" onclick={() => sendPasswordByEmail()}>{$_('Send email')}</button>
                                    </div>
                                {:else if emailPwStatus == 'working'}
                                    <div class="text-info">{$_('Sending email…')}</div>
                                {:else if emailPwStatus == 'sent'}
                                    <div class="text-success">{$_('Email sent!')}</div>
                                {/if}
                            </div>
                        </li>

                        {#if $userGroups.has('wdom-user-signin')}
                            <li>
                                {#if signinToken === 'working'}
                                    <div class="text-info">
                                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                        {$_('Generating a new auto-signin token…')}
                                    </div>
                                {:else if signinToken == null}
                                    <button class="btn btn-link btn-a" type="button" onclick={() => generateSigninToken()}>{$_('Generate an auto-signin token to enter the client area as this user.')}</button>
                                {:else}
                                    <Icon inline icon="fa:check" />
                                    <a href="{WDATA['public-site-url']}/auto-signin/{signinToken}" target="_blank" class="text-danger" onclick={() => clearSigninToken()}>{$_('Sign into the client site as this user.')}</a>
                                {/if}
                            </li>
                        {/if}
                    </ul>
                </dd>

                <dt>{$_('Capabilities')}</dt>
                <dd>
                    <ul class="list-unstyled">
                        {#if activeWorking}
                            <li class="text-info">
                                <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                {#if $user.active}{$_('Deactivating the user…')}
                                {:else}{$_('Activating the user…')}
                                {/if}
                            </li>
                        {:else if $user.active}
                            <li class="text-success">
                                <Icon inline icon="fa:toggle-on" />
                                {$_('The user is active and can sign in.')}
                                <button class="btn btn-link btn-a" type="button" onclick={() => setUserActive(false)}><span class="text-warning">{$_('(deactivate)')}</span></button>
                            </li>
                        {:else}
                            <li class="text-danger">
                                <Icon inline icon="fa:toggle-off" />
                                {$_('The user is not active and cannot sign in.')}
                                <button class="btn btn-link btn-a" type="button" onclick={() => setUserActive(true)}><span>{$_('(activate)')}</span></button>
                            </li>
                            {#if $userGroups.has('wdom-user-admin') && $userGroups.has('wdom-user-delete')}
                                <li style="display: flex; gap: 2em; align-items: center;">
                                    <div style="flex: 1 0 0;">
                                        {#if deletingUser == 'working'}
                                            <div class="text-info">
                                                <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                                Suppression de l’utilisateur…
                                            </div>
                                        {:else}
                                            <span class:text-muted={deletingUser == 'confirm'} class:text-warning={deletingUser == null}><Icon inline icon="fa:trash-o" /></span>
                                            <button class="btn btn-link btn-a" type="button" onclick={() => (deletingUser = 'confirm')}>
                                                <span class:text-muted={deletingUser == 'confirm'} class:text-warning={deletingUser == null}>Supprimer cet utilisateur.</span>
                                            </button>
                                        {/if}
                                    </div>
                                </li>
                            {/if}
                        {/if}
                    </ul>
                </dd>

                <dt>{$_('Jetons (API)')}</dt>
                <dd>
                    <ul class="list-unstyled">
                        <li class="">{$ngettext('{n} jeton', '{n} jetons', { n: $user.n_api_tokens })}</li>
                    </ul>
                </dd>

                {#if $userGroups.has('global-ecom-admin')}
                    <dt>{$_('Ecom')}</dt>
                    <dd>
                        <ul class="list-unstyled">
                            <li>
                                {#if creatingOrder}
                                    <div class="text-info">
                                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                        Création d’une commande…
                                    </div>
                                {:else}
                                    <button type="button" class="btn btn-link btn-a" onclick={() => createOrder()}>{$_('Create a new order')}</button>
                                {/if}
                            </li>
                            <li>
                                <a use:sanchor href={spath(`/ecom/tld-config/${$user.id}`)}>{$_('Edit user’s custom prices')}</a>
                            </li>
                            <li>
                                {#if ecomStatsWorking}
                                    <a use:sanchor href={spath(`/ecom/orders?q=${$user.id}`)}>{$_('Afficher les commandes de cet utilisateur')}</a>
                                    <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                {:else}
                                    <a use:sanchor href={spath(`/ecom/orders?q=${$user.id}`)}>{$ngettext('Afficher la commande de cet utilisateur', 'Afficher les {n} commandes de cet utilisateur', { n: ecomTotal })}</a>
                                    {#if Object.keys(ecomStats).length}
                                        <span style="display: block; margin-left: 1em; margin-right: 1em;">
                                            (<span class="small">
                                                {#each Object.entries(ecomStats) as [key, cnt]}
                                                    {#if cnt > 0}
                                                        <span class="enum"><a use:sanchor href={spath(`/ecom/orders?q=${$user.id}&status=${key}`)}>{cnt} “{$resolve(statusText(key, true))}”</a></span>
                                                    {/if}
                                                {/each}
                                            </span>)
                                        </span>
                                    {/if}
                                {/if}
                            </li>
                        </ul>
                    </dd>
                {/if}

                <StatusBlockNotifications />
            </dl>
        </div>
    {/if}
</div>

{#if deletingUser == 'confirm'}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>{$_('Détruire l’utilisateur')}</div>
                </div>
                <div class="modal-body">
                    <textarea class="form-control" bind:value={deleteReason}></textarea>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-outline-secondary" onclick={() => (deletingUser = undefined)}>{$_('Cancel')}</button>
                    <button type="button" class="btn btn-outline-danger" onclick={() => deleteUser()}>{cleanDeleteReason != '' ? $_('Détruire avec une raison') : $_('Détruire')}</button>
                </div>
            </div>
        </div>
        <div class="modal-backdrop" onclick={() => (deletingUser = undefined)} aria-hidden="true"></div>
    </div>
{/if}
